import {Box, Col, Row} from 'components/Grid';
import React, {FC, useContext, useMemo} from 'react';
import style from './style.module.scss';
import {
    PRODUCT_REVIEW_HASH_PREFIX,
    PRODUCT_REVIEW_SCROLL_DELAY,
    PRODUCT_REVIEW_SCROLL_OFFSET,
} from 'modules/products/constants';
import {ProductRatingCard} from 'modules/products/components/ProductRatingCard';
import classNames from 'classnames';
import {useAutoScrollByUrlHash} from 'core/hooks/useAutoScrollByUrlHash';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {ProductHeader} from 'modules/products/components/ProductHeader';
import {ProductReviews} from 'modules/products/components/ProductReviews';
import {ProductPriceBlock} from 'modules/product-card/components/ProductPriceBlock';
import {MediaMagnifierSlider} from 'modules/slider/components/MediaMagnifierSlider';
import {ProductBuyMobilePanel} from 'modules/product-card/components/ProductBuyMobilePanel';
import {ProductCharacteristics} from 'modules/products/pages/ProductReviewPage/components/ProductCharacteristics';
import {ProductNotifyAvailability} from 'modules/product-card/components/ProductNotifyAvailability';
import {getAdditionalDataCatalog} from 'modules/products/helpers';
import {HeaderStickyPortal} from 'components/Header/BasicHeader/components/HeaderStickyPortal';
import {ProductTopDesktopPanelContainer} from 'modules/products/components/ProductTopDesktopPanelContainer';
import {TrustSlider} from 'modules/slider/components/TrustSlider';
import {useProductImagesAltText} from 'modules/product-card/hooks/useProductImagesAltText';
import {PageHead} from 'core/modules/PageHead';
import {ProductReviewContext} from 'modules/products/context/ProductReview';
import {ProductStickyHeaderPageName} from 'modules/product-card/constants';
import {ProductSchema} from 'modules/product-card/components/Marking';
import {NEXT_PUBLIC_MASTER_HOST} from 'core/helpers/env';
import {Breadcrumbs} from 'modules/breadcrumbs/components/Breadcrumbs';
import {IBreadcrumb} from 'modules/seo/types';
import {PAGES} from 'core/modules/routing/constants';
import ProductModalsBlock from 'modules/product-card/pages/ProductPage/ProductModalsBlock';

interface IReviewsPageContentProps {
    breadcrumbs: IBreadcrumb[];
}

// eslint-disable-next-line max-lines-per-function,complexity
export const ProductReviewPage: FC<IReviewsPageContentProps> = ({breadcrumbs = []}) => {
    const productData = useAppSelector(selectProductData);
    const {labels, id, catalogRusName, properties, media} = productData || {};

    const additionalDataCatalog = getAdditionalDataCatalog(catalogRusName, properties);
    const {data} = useContext(ProductReviewContext);
    const seo = data?.seo;

    const productImages = useMemo(() => {
        const {imagePreview, images} = media || {};
        // eslint-disable-next-line no-nested-ternary
        return imagePreview ? [imagePreview, ...(images || [])] : images ? [...images] : [];
    }, [media]);

    const isShowNotifyAvailability = useMemo(() => {
        if (!productData) {
            return false;
        }

        const {canBuy, canReserve} = productData;

        return !canBuy && !canReserve;
    }, [productData]);

    useAutoScrollByUrlHash({
        delay: PRODUCT_REVIEW_SCROLL_DELAY,
        hashPrefix: PRODUCT_REVIEW_HASH_PREFIX,
        offset: PRODUCT_REVIEW_SCROLL_OFFSET,
    });

    const sliderImageAltText = useProductImagesAltText();

    const productPriceHideMap = {
        receivingMethod: true,
    };

    return (
        <>
            <PageHead title={seo?.metaTitle} description={seo?.metaDescription} robots={seo?.robots} />
            <ProductSchema url={`${NEXT_PUBLIC_MASTER_HOST}/catalog/product/${productData?.code}/reviews`} />
            <Box>
                <Breadcrumbs
                    breadcrumbs={[
                        ...breadcrumbs,
                        {
                            name: PAGES.PRODUCT_REVIEWS_PAGE.title,
                            url: PAGES.PRODUCT_REVIEWS_PAGE.url,
                        },
                    ]}
                />
            </Box>
            <Box className={style.ratingSection}>
                <ProductHeader id={id} catalogRusName={additionalDataCatalog} labels={labels} title={seo?.pageTitle} />
                <div className={classNames(style.productGalleryWrapper, style.mobile)}>
                    <MediaMagnifierSlider
                        bubble={productData?.bubble}
                        images={productImages}
                        onlyThumbsSlider={true}
                        popupDirection={'right'}
                        thumbSliderDirection={'horizontal'}
                        thumbSlidesPerView={4}
                        imagesAltText={sliderImageAltText}
                        isReviewsPage={true}
                    />
                </div>

                <Row className={style.ratingCardWrapper}>
                    <Col lg={8} sm={12}>
                        <ProductRatingCard />
                        <div className={classNames(style.productCard, style.productTablet, style.tablet)}>
                            <div className={style.mediaWrapperTablet}>
                                <div className={style.item}>
                                    <div className={style.product}>
                                        <MediaMagnifierSlider
                                            bubble={productData?.bubble}
                                            hideThumbIfOneImage={true}
                                            images={productImages}
                                            popupDirection={'right'}
                                            thumbSliderDirection={'horizontal'}
                                            thumbSlidesPerView={4}
                                            imagesAltText={sliderImageAltText}
                                            isReviewsPage={true}
                                        />
                                        {isShowNotifyAvailability ? (
                                            <ProductNotifyAvailability />
                                        ) : (
                                            <ProductPriceBlock hideMap={productPriceHideMap} />
                                        )}
                                    </div>
                                </div>
                                <ProductCharacteristics
                                    className={classNames(style.productCharacteristics, style.item)}
                                />
                            </div>
                        </div>
                        <ProductCharacteristics className={classNames(style.productCharacteristicsMobile)} />
                        <div className={classNames(style.listRow, style.commentsWrapper)}>
                            <ProductReviews />
                        </div>
                    </Col>
                    <Col className={style.desktop} lg={4} sm={12}>
                        <div className={style.productCard}>
                            <div className={style.product}>
                                <MediaMagnifierSlider
                                    bubble={productData?.bubble}
                                    hideThumbIfOneImage={true}
                                    images={productImages}
                                    popupDirection={'right'}
                                    thumbSliderDirection={'horizontal'}
                                    thumbSlidesPerView={4}
                                    imagesAltText={sliderImageAltText}
                                    isReviewsPage={true}
                                />
                                {isShowNotifyAvailability ? (
                                    <ProductNotifyAvailability />
                                ) : (
                                    <ProductPriceBlock hideMap={productPriceHideMap} />
                                )}
                            </div>
                            <ProductCharacteristics className={style.productCharacteristics} />
                        </div>
                    </Col>
                </Row>
            </Box>

            <TrustSlider />

            <ProductBuyMobilePanel />

            <ProductModalsBlock />

            <HeaderStickyPortal>
                <ProductTopDesktopPanelContainer page={ProductStickyHeaderPageName.Review} />
            </HeaderStickyPortal>
        </>
    );
};
